import React, {useEffect, useState} from 'react';
import {
  IFarmInputInvoice,
  IRouteScreenProps,
  ISupplierUserProfile,
  IUser,
} from 'constants/interfaces';
import {apiGetDepotOrders, apiGetUser} from 'constants/Api';
import {
  Box,
  Button,
  ButtonGroup,
  Snackbar,
  Typography,
} from '@material-ui/core';
import {connect} from 'react-redux';
import {Route, useLocation} from 'react-router-dom';
import NavigationService from 'services/NavigationService';
import {
  ActiveFilterState,
  KhulaSupplierTable,
  Row,
} from 'components/Table/KhulaSupplierTable';
import {
  getDefaultPaidFilter,
  invoiceTableFilters,
  invoiceTableHeaders,
  invoiceTableRows,
} from 'screens/Dashboard/Invoices/InvoiceTableSetup';
import {InvoiceState} from 'appredux/reducers/reducer-invoices';
import moment from 'moment';
import {reduxSetInvoices} from 'appredux/actions';
import {PermissionsService} from 'services/PermissionsService';
import {ESupplierUserPermissions} from 'constants/index';

type InvoicesProps = IRouteScreenProps & {
  user: ISupplierUserProfile;
  invoiceState: InvoiceState;
};

class Invoices extends React.Component<InvoicesProps> {
  state: {
    isLoading: boolean;
    isRefreshing: boolean;
    showRefreshedSnackbar: boolean;
    fetchSize: number;
    user?: IUser;
    farmerId?: string;
  } = {
    isLoading: true,
    isRefreshing: false,
    showRefreshedSnackbar: false,
    fetchSize: 20,
    user: undefined,
    farmerId: undefined,
  };

  componentDidMount() {
    const queryParams = new URLSearchParams(this.props.location.search);
    const userId = queryParams.get('userId');
    const noInvoices = !this.props.invoiceState.invoices?.length;

    Promise.resolve()
      .then(() => {
        if (userId)
          return apiGetUser(userId).catch((err) => {
            console.error('Failed to fetch user:', err);
          });
      })
      .then((user) => {
        this.setState(
          {
            isLoading: noInvoices,
            user,
            farmerId: user?.farmer?.id,
          },
          () => this.loadInvoices(),
        );
      });
  }

  loadInvoices() {
    const {invoiceState} = this.props;
    const {lastUpdate, invoices} = invoiceState;

    const invoicesAreExpired = moment(lastUpdate).isBefore(
      moment().subtract(30, 'minutes'),
    );
    const noInvoices = !invoices?.length;

    if (invoicesAreExpired || noInvoices || this.state.farmerId)
      this.refreshInvoices();
  }

  refreshInvoices() {
    const {fetchSize, farmerId} = this.state;

    this.setState({isRefreshing: true}, () => {
      apiGetDepotOrders(fetchSize, farmerId)
        .then((invoices: IFarmInputInvoice[]) => {
          reduxSetInvoices(invoices);
          this.setState({showRefreshedSnackbar: true});
        })
        .catch((err) => {
          alert('Failed to load invoices. Please try again later');
          console.error(err);
        })
        .finally(() => this.setState({isLoading: false, isRefreshing: false}));
    });
  }

  handleFetchSizeUpdate(size?: number) {
    this.setState({fetchSize: size}, () => {
      this.refreshInvoices();
    });
  }

  render() {
    const {invoiceState} = this.props;
    const {lastUpdate, invoices} = invoiceState;
    const {isLoading, isRefreshing, showRefreshedSnackbar, fetchSize, user} =
      this.state;

    const isAdmin = this.props.user.isAdmin;
    const permissionsService = new PermissionsService(this.props.user);
    const canWriteInvoices = permissionsService.hasPermission(
      ESupplierUserPermissions.ORDER_WRITE,
    );
    const canManageInvoicePayments = permissionsService.hasPermission(
      ESupplierUserPermissions.ORDER_MANAGE_PAYMENTS,
    );

    const userFullName = user ? `${user.name} ${user.surname}` : '';

    return (
      <>
        <div className="table-screen invoices-container">
          <Typography variant="h4" className="page-title">
            Invoices{userFullName ? `: ${userFullName}` : ''}
          </Typography>
          <Typography variant="body1" className="page-subtitle">
            Your depot invoices
          </Typography>
          <Route
            render={() => (
              <div className="invoices-section">
                <Box className="actions-row">
                  <div style={{flex: 1}} />
                  <ButtonGroup variant="text">
                    {[20, 50, undefined].map((size, index) => (
                      <Button
                        key={index}
                        variant={fetchSize === size ? 'contained' : 'text'}
                        disabled={isLoading || isRefreshing}
                        disableElevation
                        onClick={() => this.handleFetchSizeUpdate(size)}
                        size={'small'}
                      >
                        {size || 'All'}
                      </Button>
                    ))}
                  </ButtonGroup>
                </Box>
                <InvoicesTable
                  isAdmin={isAdmin}
                  canWriteInvoices={canWriteInvoices}
                  canManageInvoicePayments={canManageInvoicePayments}
                  invoices={invoices}
                  isLoading={isLoading}
                  isRefreshing={isRefreshing}
                  onRefresh={() => this.refreshInvoices()}
                />
                <Box mt={1}>
                  <Typography variant={'caption'}>
                    Last updated: {moment(lastUpdate).fromNow()}
                  </Typography>
                </Box>
                <Snackbar
                  anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                  open={showRefreshedSnackbar}
                  onClose={() => this.setState({showRefreshedSnackbar: false})}
                  autoHideDuration={3000}
                  message="Invoices refreshed"
                />
              </div>
            )}
          />
        </div>
      </>
    );
  }
}

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const InvoicesTable = (props: {
  isAdmin: boolean;
  canWriteInvoices: boolean;
  canManageInvoicePayments: boolean;
  invoices: IFarmInputInvoice[];
  isLoading: boolean;
  isRefreshing: boolean;
  onRefresh: VoidFunction;
}) => {
  const {
    invoices,
    isAdmin,
    canWriteInvoices,
    canManageInvoicePayments,
    isLoading,
    isRefreshing,
    onRefresh,
  } = props;

  const query = useQuery();
  const [defaultActiveFilters, setDefaultActiveFilters] = useState<
    ActiveFilterState[]
  >([]);

  useEffect(() => {
    const activeFilters = [];
    const paidString = query.get('paid');

    if (paidString) {
      activeFilters.push(getDefaultPaidFilter(paidString === '1'));
    }

    setDefaultActiveFilters(activeFilters);
  }, [query?.get('paid')]);

  const mapRowToInvoice = (row: Row) =>
    // @ts-ignore
    invoices.find((invoice) => invoice.id == row.id);

  return (
    <KhulaSupplierTable
      headers={invoiceTableHeaders(isAdmin)}
      rows={invoiceTableRows(
        canWriteInvoices,
        canManageInvoicePayments,
        invoices,
        (invoice) => {
          NavigationService.navigate(`/orders/${invoice.id}`);
        },
      )}
      filtersGroups={invoiceTableFilters}
      defaultActiveFilters={defaultActiveFilters}
      isLoading={isLoading}
      isRefreshing={isRefreshing}
      onRefresh={onRefresh}
      onRowSelect={(row) => {
        const invoice = mapRowToInvoice(row);
        if (!invoice) return;
        NavigationService.navigate(`/orders/${invoice.id}`);
      }}
    />
  );
};

const mapStateToProps = (state: any) => {
  return {
    user: state.user,
    invoiceState: state.invoices,
  };
};
export default connect(mapStateToProps)(Invoices);
